import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { authHref, possessiveSchool, useSchoolBrand } from '../auth/schoolLink';
import { hasPendingGoogleOAuth, signInWithApple, signInWithGoogle } from '../auth/socialSignIn';
import {
  AuthScreen,
  BrandHeader,
  PasswordField,
  SocialButtons,
  errorText,
  fieldText,
} from '../components/AuthChrome';
import '../styles/AuthScreen.css';

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
}

const Signup = ({ onAuthChange }) => {
  const brand = useSchoolBrand();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [schools, setSchools] = useState([]);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brand.ready || brand.isSchool) return undefined;
    let cancelled = false;
    apiService
      .getPublicSchools()
      .then((data) => {
        if (cancelled) return;
        setSchools(Array.isArray(data?.results) ? data.results : []);
      })
      .catch(() => {
        if (!cancelled) setSchools([]);
      });
    return () => {
      cancelled = true;
    };
  }, [brand.ready, brand.isSchool]);

  const matches = useMemo(() => {
    const query = schoolQuery.trim().toLowerCase();
    if (query.length < 1) return [];
    return schools
      .filter((school) => String(school.name || '').toLowerCase().includes(query))
      .slice(0, 8);
  }, [schoolQuery, schools]);

  const finish = async (session, schoolId) => {
    if (session.created && schoolId) {
      try {
        await apiService.assignSchool(schoolId);
      } catch (assignError) {
        setErrors({ general: errorText(assignError, 'Account created, but the school could not be saved.') });
        onAuthChange && onAuthChange();
        return;
      }
    }
    onAuthChange && onAuthChange();
    navigate('/');
  };

  const chosenSchoolId = brand.isSchool ? brand.schoolId : selectedSchool?.id || '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const name = splitName(fullName);
    if (!name) nextErrors.fullName = 'Enter your first and last name.';
    if (!brand.isSchool && !selectedSchool) nextErrors.school = 'Search for your university.';
    if (password.length < 8) nextErrors.password = 'Use at least 8 characters.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      const payload = {
        email: email.trim(),
        first_name: name.first_name,
        last_name: name.last_name,
        password,
        password_confirm: password,
        accepted_terms_and_policies: true,
      };
      if (chosenSchoolId) payload.school_id = chosenSchoolId;
      const session = await apiService.register(payload);
      await finish({ ...session, created: false }, '');
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        setErrors({
          general: fieldText(data.non_field_errors) || fieldText(data.detail) || '',
          email: fieldText(data.email),
          password: fieldText(data.password),
          fullName: fieldText(data.first_name) || fieldText(data.last_name),
          school: fieldText(data.school_id),
        });
      } else {
        setErrors({ general: errorText(error, 'Could not create your account.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setErrors({});
    try {
      const idToken = await signInWithGoogle();
      const session = await apiService.googleLogin(idToken);
      await finish(session, chosenSchoolId);
    } catch (error) {
      if (!error.cancelled) {
        setErrors({ general: errorText(error, error.message || 'Google sign-in failed.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const googleRef = useRef(handleGoogle);
  googleRef.current = handleGoogle;

  useEffect(() => {
    if (hasPendingGoogleOAuth()) googleRef.current();
  }, []);

  const handleApple = async () => {
    setLoading(true);
    setErrors({});
    try {
      const apple = await signInWithApple();
      const session = await apiService.appleLogin(apple);
      await finish(session, chosenSchoolId);
    } catch (error) {
      if (!error.cancelled) {
        setErrors({ general: errorText(error, error.message || 'Apple sign-in failed.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const subtitle = brand.isSchool
    ? `Join ${possessiveSchool(brand.name)} fans and get your first 250 pts.`
    : 'No extra cost. No donations. Just the purchases you already make.';

  return (
    <AuthScreen brand={brand}>
      <BrandHeader brand={brand} />
      <h1 className="auth-title">Create your account</h1>
      <p className="auth-subtitle">{subtitle}</p>

      {errors.general ? <div className="auth-alert">{errors.general}</div> : null}

      <SocialButtons onGoogle={handleGoogle} onApple={handleApple} disabled={loading} />

      <div className="auth-divider">or sign up with email</div>

      <form onSubmit={handleSubmit}>
        {brand.isSchool ? null : (
          <div className="auth-field">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor="signup-school">
                School
              </label>
            </div>
            <div className="auth-search">
              <svg className="auth-search-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                id="signup-school"
                className="auth-input"
                type="text"
                value={schoolQuery}
                placeholder="Search for your university"
                autoComplete="off"
                onChange={(event) => {
                  setSchoolQuery(event.target.value);
                  setSelectedSchool(null);
                  setSchoolOpen(true);
                  if (errors.school) setErrors({ ...errors, school: '' });
                }}
                onFocus={() => setSchoolOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => setSchoolOpen(false), 150);
                }}
              />
              {schoolOpen && matches.length ? (
                <ul className="auth-suggest">
                  {matches.map((school) => (
                    <li key={school.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSchool(school);
                          setSchoolQuery(school.name);
                          setSchoolOpen(false);
                        }}
                      >
                        {school.name}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {errors.school ? <p className="auth-field-error">{errors.school}</p> : null}
          </div>
        )}

        <div className="auth-split">
          <div className="auth-field">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor="signup-name">
                Full name
              </label>
            </div>
            <input
              id="signup-name"
              className="auth-input"
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Alex Smith"
              autoComplete="name"
              required
            />
            {errors.fullName ? <p className="auth-field-error">{errors.fullName}</p> : null}
          </div>
          <div className="auth-field">
            <div className="auth-label-row">
              <label className="auth-label" htmlFor="signup-email">
                Email address
              </label>
            </div>
            <input
              id="signup-email"
              className="auth-input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              required
            />
            {errors.email ? <p className="auth-field-error">{errors.email}</p> : null}
          </div>
        </div>

        <PasswordField
          id="signup-password"
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Password"
          autoComplete="new-password"
          error={errors.password}
          hint="Use at least 8 characters"
        />

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading ? 'Creating account…' : 'Create account · +250 pts'}
        </button>
      </form>

      <p className="auth-legal">
        By creating an account, you agree to our <Link to="/terms-and-conditions/">Terms and Conditions</Link> &{' '}
        <Link to="/privacy-policy/">Privacy Policy</Link>
      </p>

      <p className="auth-footer">
        Already have an account? <Link to={authHref('/login', brand.schoolId)}>Log in</Link>
      </p>
    </AuthScreen>
  );
};

export default Signup;
